import React, { useEffect, useRef, useState } from 'react'
import { Input, Button, Typography, Form, Flex } from 'antd'
import DefaultFlowMessage from 'features/app-flows/components/DefaultFlowMessage'
import { RightOutlined } from '@ant-design/icons'
import { authStorage } from 'app/auth-storage'
import { useUpdateUserMutation } from 'api/users-api'
import { Pto } from '@outcoreaix/pto'

const { Text } = Typography

interface AddNameDetailsProps {
  blobAvatarUrl: string
  onFinish: () => void
}

const AddNameDetails: React.FC<AddNameDetailsProps> = ({ blobAvatarUrl, onFinish }) => {
  const [updateUser] = useUpdateUserMutation()
  const [form] = Form.useForm()
  const [isTyping, setIsTyping] = useState(true)
  const calledRef = useRef(false)

  useEffect(() => {
    const nameNeeded = authStorage.getPendingUserNameSet() || authStorage.userNameSetNeeded()
    if (!nameNeeded) {
      calledRef.current = true
      onFinish()
    }
  }, [onFinish])

  useEffect(() => {
    setIsTyping(true)
    const timeout = setTimeout(() => setIsTyping(false), 1000)
    return () => clearTimeout(timeout)
  }, [])

  const handleSubmit = () => {
    const values = form.getFieldsValue()
    const { firstName, lastName } = values
    if (firstName && lastName) {
      void updateUser({ firstName, lastName })
        .unwrap()
        .then(async (jwtUser: Pto.Auth.JwtUser) => {
          authStorage.setUser(jwtUser)
          if (!authStorage.userNameSetNeeded()) authStorage.setPendingUserNameSet(false)

          if (!calledRef.current) {
            calledRef.current = true
            onFinish()
          }
        })
        .catch(() => {})
    }
  }

  return (
    <Flex className="flex-col justify-start items-center w-full overflow-x-hidden vsmall:overflow-y-hidden h-full vsmall:min-h-0 p-5 vsmall:p-2">
      <div
        className={`full-width-message flex flex-col h-full max-w-[870px] mx-auto gap-1 vsmall:min-h-0 vtall:justify-evenly vtall:md:justify-center vtall:min-h-[600px] vtall:lg:max-h-[700px] vtall:lg:mt-[50px]`}
      >
        <div className="vsmall:flex-1 vsmall:min-h-0 vsmall:overflow-y-auto scrollbar-thin">
          <DefaultFlowMessage
            blobAvatarUrl={blobAvatarUrl}
            isTyping={isTyping}
            question={<div className="font-semibold text-[20px] lg:text-[24px]">What‘s your name?</div>}
            body={
              <div className="mt-2 ">
                <Text className="text-lg">
                  I’m Alex and I’m excited to help with your career journey. Please enter your name.
                </Text>
              </div>
            }
          />
          {!isTyping && (
            <Form
              id="name-form"
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="mt-6 vsmall:mt-2 lg:ml-[64px]"
            >
              <div>
                <Form.Item
                  className="!text-base"
                  name="firstName"
                  label="First name"
                  rules={[{ required: true, message: 'Please enter your first name' }]}
                >
                  <Input
                    placeholder="e.g. John"
                    className="hover:!border-[var(--colorBorder)] focus:!border-[var(--colorBorder)] focus:!shadow-[0_0_0_2px_var(--colorBorder)]"
                  />
                </Form.Item>

                <Form.Item
                  name="lastName"
                  label="Last name"
                  rules={[{ required: true, message: 'Please enter your last name' }]}
                >
                  <Input
                    placeholder="e.g. Smith"
                    className="hover:!border-[var(--colorBorder)] focus:!border-[var(--colorBorder)] focus:!shadow-[0_0_0_2px_var(--colorBorder)]"
                  />
                </Form.Item>
              </div>
            </Form>
          )}
        </div>

        {!isTyping && (
          <div className="basis-1/7 vsmall:shrink-0 flex gap-2 items-center justify-center lg:justify-end pb-3 vsmall:pb-2">
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              className="!px-6 !py-3 mt-4 vsmall:mt-1 !text-lg w-full max-w-[330px] lg:w-[220px] !rounded-lg min-h-[52px] !bg-primary !border-primary text-white shadow-none hover:!opacity-80"
              icon={<RightOutlined />}
              iconPosition="end"
              htmlType="submit"
              form="name-form"
            >
              Add my details
            </Button>
          </div>
        )}
      </div>
    </Flex>
  )
}

export default AddNameDetails
